import { FC, useCallback, useRef, useState } from 'react';

import HeroBlock from '../HeroBlock';
import QuizStepBlock from '../QuizStepBlock';
import EmailCaptureBlock from '../EmailCaptureBlock';
import TeamInputBlock from '../TeamInputBlock';
import LoadingBlock from '../LoadingBlock';
import ResultBlock from '../ResultBlock';
import HandoffBlock from '../HandoffBlock';
import CtaBlock from '../CtaBlock';
import PaywallBlock from '../PaywallBlock';
import GytrProvider from '../GytrProvider';
import ReadinessProvider from '../ReadinessProvider';
import ReadinessFlowController from '../ReadinessSteps/ReadinessFlowController';
import GytrFlowController from '../GytrSteps/GytrFlowController';
import QuizProgressBar from '../QuizProgressBar';
import { useCaptureCouponFromUrl } from '@hooks/useCaptureCouponFromUrl';
import { useFunnelEmailCapture } from '@hooks/useFunnelEmailCapture';
import useQuizHashTracking from '@hooks/useQuizHashTracking';
import {
  countQuizSteps,
  generateMockResult,
  getQuizStepIndex,
  INITIAL_QUIZ_STATE,
  QuizState
} from '@lib/page-utils/Quiz';
import { isReadinessStepFlow } from '@lib/page-utils/Readiness/progress';
import { isGytrStepFlow } from '@lib/page-utils/Gytr/progress';
import type { SanityQuizBlock, SanityQuizPage } from '@lib/sanity/types';

type QuizBlockRendererProps = {
  quizPage: SanityQuizPage;
};

const QuizBlockRenderer: FC<QuizBlockRendererProps> = ({ quizPage }) => {
  const { blocks, theme } = quizPage;
  const [quizState, setQuizState] = useState<QuizState>(INITIAL_QUIZ_STATE);
  const [isLlmLoading, setIsLlmLoading] = useState(false);
  const [hasLlmError, setHasLlmError] = useState(false);
  const answersRef = useRef(quizState.answers);
  answersRef.current = quizState.answers;

  const emailCaptureMutation = useFunnelEmailCapture();

  // FFH-1769: remember `?coupon=` on funnel entry so it reaches checkout
  useCaptureCouponFromUrl();

  const handleGoToStep = useCallback((index: number) => {
    setQuizState((previous) => ({ ...previous, currentStepIndex: index }));
  }, []);

  useQuizHashTracking(blocks, quizState.currentStepIndex, handleGoToStep);

  const totalQuizSteps = countQuizSteps(blocks);
  const currentQuizStep = getQuizStepIndex(blocks, quizState.currentStepIndex);
  const isCurrentBlockQuizStep =
    blocks[quizState.currentStepIndex]?._type === 'quizStepBlock';

  const handleNextStep = useCallback(() => {
    setQuizState((previous) => {
      const nextIndex = previous.currentStepIndex + 1;
      if (nextIndex >= blocks.length) return previous;

      const nextBlock = blocks[nextIndex];
      if (nextBlock._type === 'loadingBlock') {
        generateResult();
      }

      return { ...previous, currentStepIndex: nextIndex };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks]);

  const handleBack = useCallback(() => {
    setQuizState((previous) => {
      const previousIndex = previous.currentStepIndex - 1;
      if (previousIndex < 0) return previous;
      return { ...previous, currentStepIndex: previousIndex };
    });
  }, []);

  const handleQuizAnswer = (stepNumber: number, value: string) => {
    setQuizState((previous) => ({
      ...previous,
      answers: { ...previous.answers, [stepNumber]: value }
    }));
  };

  const handleQuizAnswerAutoAdvance = (stepNumber: number, value: string) => {
    const AUTO_ADVANCE_DELAY_MS = 500;

    setQuizState((previous) => ({
      ...previous,
      answers: { ...previous.answers, [stepNumber]: value }
    }));

    setTimeout(() => {
      handleNextStep();
    }, AUTO_ADVANCE_DELAY_MS);
  };

  const handleEmailSubmit = (email: string) => {
    setQuizState((previous) => ({
      ...previous,
      isEmailCaptured: true,
      email
    }));

    emailCaptureMutation.mutate({
      email,
      funnelSlug: quizPage.templateKey || quizPage.slug.current
    });
    handleNextStep();
  };

  const generateResult = async () => {
    setIsLlmLoading(true);
    setHasLlmError(false);

    try {
      const result = await generateMockResult(answersRef.current);

      setQuizState((previous) => {
        const loadingBlockIndex = previous.currentStepIndex;
        const resultBlockIndex = loadingBlockIndex + 1;

        return {
          ...previous,
          llmResult: result,
          currentStepIndex:
            resultBlockIndex < blocks.length
              ? resultBlockIndex
              : previous.currentStepIndex
        };
      });
    } catch {
      setHasLlmError(true);
    } finally {
      setIsLlmLoading(false);
    }
  };

  const handleShare = async () => {
    const resultName = quizState.llmResult?.name ?? '';
    const shareData = {
      title: 'My FPL Team Name',
      text: `My FPL team name is: ${resultName}`,
      url: window.location.href
    };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled or share failed silently
      }
    } else {
      await navigator.clipboard.writeText(
        `${shareData.text}\n${shareData.url}`
      );
    }
  };

  const renderBlock = (
    block: SanityQuizBlock,
    blockIndex: number,
    isCurrentBlock: boolean
  ) => {
    switch (block._type) {
      case 'heroBlock':
        return (
          <HeroBlock key={block._key} {...block} onCtaClick={handleNextStep} />
        );

      case 'quizStepBlock': {
        if (!isCurrentBlock) return null;

        const hasExplicitNext = Boolean(block.nextButtonText);

        return (
          <QuizStepBlock
            key={block._key}
            {...block}
            theme={theme}
            selectedValue={quizState.answers[blockIndex]}
            onAnswer={
              hasExplicitNext
                ? (value) => handleQuizAnswer(blockIndex, value)
                : (value) => handleQuizAnswerAutoAdvance(blockIndex, value)
            }
            onNext={hasExplicitNext ? handleNextStep : undefined}
            onBack={quizState.currentStepIndex > 1 ? handleBack : undefined}
          />
        );
      }

      case 'teamInputBlock':
        return isCurrentBlock ? (
          <TeamInputBlock
            key={block._key}
            {...block}
            theme={theme}
            onSubmit={() => handleNextStep()}
          />
        ) : null;

      case 'emailCaptureBlock':
        return isCurrentBlock ? (
          <EmailCaptureBlock
            key={block._key}
            {...block}
            onSubmit={handleEmailSubmit}
            isLoading={emailCaptureMutation.isLoading}
            isSubmitted={quizState.isEmailCaptured}
          />
        ) : null;

      case 'loadingBlock':
        return isCurrentBlock ? (
          <LoadingBlock
            key={block._key}
            {...block}
            theme={theme}
            isLoading={isLlmLoading}
            hasError={hasLlmError}
          />
        ) : null;

      case 'resultBlock':
        return isCurrentBlock ? (
          <ResultBlock
            key={block._key}
            {...block}
            theme={theme}
            llmResult={quizState.llmResult}
            onShare={handleShare}
            onNext={handleNextStep}
          />
        ) : null;

      case 'handoffBlock':
        return isCurrentBlock ? (
          <HandoffBlock key={block._key} {...block} theme={theme} />
        ) : null;

      case 'ctaBlock':
        return isCurrentBlock ? <CtaBlock key={block._key} {...block} /> : null;

      case 'paywallBlock':
        return isCurrentBlock ? (
          <PaywallBlock
            key={block._key}
            {...block}
            theme={theme}
            quizState={quizState}
            templateKey={quizPage.templateKey}
          />
        ) : null;

      default:
        return null;
    }
  };

  const isGytrTemplate = quizPage.templateKey?.startsWith('gytr-');
  const isReadinessTemplate = quizPage.templateKey?.startsWith('readiness-');
  const isStepFlow = isReadinessStepFlow(blocks);
  const isGytrStep = isGytrStepFlow(blocks);

  const blockContent = (
    <div className="relative z-20">
      {isCurrentBlockQuizStep && (
        <QuizProgressBar
          currentQuizStep={currentQuizStep}
          totalQuizSteps={totalQuizSteps}
          isShowingLabel
        />
      )}
      {blocks.map((block, index) => {
        const isCurrentBlock = index === quizState.currentStepIndex;

        if (!isCurrentBlock) return null;

        return renderBlock(block, index, isCurrentBlock);
      })}
    </div>
  );

  return (
    <div className="funnel-fonts relative min-h-screen font-body bg-neutral-100">
      {/* Subtle grain texture overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-10 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />

      {isGytrTemplate ? (
        <GytrProvider templateKey={quizPage.templateKey}>
          {isGytrStep ? (
            <GytrFlowController quizPage={quizPage} />
          ) : (
            blockContent
          )}
        </GytrProvider>
      ) : isReadinessTemplate ? (
        <ReadinessProvider templateKey={quizPage.templateKey}>
          {isStepFlow ? (
            <ReadinessFlowController quizPage={quizPage} />
          ) : (
            blockContent
          )}
        </ReadinessProvider>
      ) : (
        blockContent
      )}
    </div>
  );
};

export default QuizBlockRenderer;
